import { create } from "zustand";
import { Tables } from "../database.types";
import { supabaseClient } from "../shared/supabase";
import { useSeasonStore } from "./season.store";

export type Course = Tables<"courses">;

type CourseState = {
  currentCourse: Course | null;
  isLoading: boolean;
  error: Error | null;
  lastFetched: number | null;
  isInitialized: boolean;
};

type CourseStore = {
  initialize: () => Promise<void>;
  getCurrentCourse: () => Promise<void>;
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useCourseStore = create<CourseStore & CourseState>((set, get) => ({
  currentCourse: null,
  isLoading: false,
  error: null,
  lastFetched: null,
  isInitialized: false,

  initialize: async () => {
    const state = get();
    if (state.isInitialized) {
      return;
    }
    await get().getCurrentCourse();
    set({ isInitialized: true });
  },

  getCurrentCourse: async () => {
    const state = get();

    const currentSeason = useSeasonStore.getState().currentSeason;

    if (!currentSeason) {
      set({
        currentCourse: null,
        isLoading: false,
        error: null,
        lastFetched: Date.now(),
      });
      return;
    }

    // Check if we have cached data and it's still valid
    if (
      state.currentCourse &&
      state.currentCourse.season_id === currentSeason.id &&
      state.lastFetched &&
      Date.now() - state.lastFetched < CACHE_DURATION
    ) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const { data: currentCourse, error } = await supabaseClient
        .from("courses")
        .select("*")
        .eq("season_id", currentSeason.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      set({
        currentCourse,
        isLoading: false,
        error: null,
        lastFetched: Date.now(),
      });
    } catch (error) {
      set({
        isLoading: false,
        error: error as Error,
      });
      throw error; // Re-throw to handle in the UI
    }
  },
}));
